import React, { useState, useRef } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { t } from "i18next";

import TopBox from "components/CalendarBox/TopBox";
import MonthShow from "components/CalendarBox/MonthShow";
import YearShow from "components/CalendarBox/YearShow";
import ButtonComp from "components/ButtonComp";
import { setSelectedDate, setVisibleRangeCalendar } from "slices";
import { useClickOutside } from "hooks";

import ShowRange from "./ShowRange";
import DayShow from "./DayShow";

const RangeCalendarBox = ({ onHide, selectedStartDate, selectedEndDate }) => {
  const dispatch = useDispatch();

  const [focusedDate, setFocusedDate] = useState(moment());
  const [focusedStartDate, setFocusedStartDate] = useState(selectedStartDate || null);
  const [focusedEndDate, setFocusedEndDate] = useState(selectedEndDate || null);
  const [showType, setShowType] = useState("day");

  const calendarRef = useRef(null);

  useClickOutside(calendarRef, () => {
    dispatch(setVisibleRangeCalendar(false));
  });

  const onClickDate = (date) => {
    // Update the range calendar selection
    if (!focusedStartDate || date.isBefore(focusedStartDate)) {
      setFocusedStartDate(date);
      setFocusedEndDate(null);
    } else if (!focusedEndDate || date.isAfter(focusedEndDate)) {
      setFocusedEndDate(date);
    } else {
      // Reset selection if clicking an already selected date
      setFocusedStartDate(date);
      setFocusedEndDate(null);
    }
  };

  const onClickMonth = (idx) => {
    // Set the month view for both start and end date
    setFocusedDate(focusedDate.clone().month(idx));
    setShowType("day");

    if (!focusedStartDate && !focusedEndDate) {
      setFocusedStartDate(focusedStartDate?.clone().month(idx) || focusedDate.clone().month(idx));
      setFocusedEndDate(focusedEndDate?.clone().month(idx) || focusedDate.clone().month(idx));
    }

    if (focusedStartDate && !focusedEndDate) {
      setFocusedEndDate(focusedEndDate?.clone().month(idx) || focusedDate.clone().month(idx));
    }
  };

  const onClickYear = (year) => {
    // Set the year view for both start and end date
    setFocusedDate(focusedDate.clone().year(year));
    setShowType("day");

    if (!focusedStartDate && !focusedEndDate) {
      setFocusedStartDate(focusedStartDate?.clone().year(year) || focusedDate.clone().year(year));
      setFocusedEndDate(focusedEndDate?.clone().year(year) || focusedDate.clone().year(year));
    }

    if (focusedStartDate && !focusedEndDate) {
      setFocusedEndDate(focusedEndDate?.clone().year(year) || focusedDate.clone().year(year));
    }
  };

  return (
    <div className="hayley-absolute hayley-z-[9999]" ref={calendarRef}>
      <ShowRange focusedStartDate={focusedStartDate} focusedEndDate={focusedEndDate} />
      <div className="hayley-grid hayley-h-fit hayley-w-fit hayley-select-none hayley-grid-cols-7 hayley-border hayley-border-b-0 hayley-border-solid hayley-border-custom-6 hayley-bg-white hayley-p-4 hayley-pb-0">
        <TopBox focusedDate={focusedDate} showType={showType} setFocusedDate={setFocusedDate} setShowType={setShowType} />
        {showType === "day" && (
          <DayShow focusedDate={focusedDate} focusedStartDate={focusedStartDate} focusedEndDate={focusedEndDate} onClickDate={onClickDate} />
        )}
        {showType === "month" && <MonthShow onClickMonth={(idx) => onClickMonth(idx)} />}
        {showType === "year" && <YearShow onClickYear={(year) => onClickYear(year)} />}
      </div>
      <div className="hayley-flex hayley-h-20 hayley-w-full hayley-items-center hayley-justify-end hayley-border hayley-border-t-0 hayley-border-solid hayley-border-custom-6 hayley-bg-white  hayley-p-4 hayley-pt-0">
        {(focusedStartDate || focusedEndDate) && (
          <ButtonComp
            label={t("reset")}
            onClick={() => {
              setFocusedStartDate(null);
              setFocusedEndDate(null);
            }}
            disabled={false}
            border={false}
          />
        )}

        <ButtonComp
          label={t("apply")}
          onClick={() => {
            dispatch(setSelectedDate(`${focusedStartDate.format("YYYY-MM-DD")},${focusedEndDate.format("YYYY-MM-DD")}`));

            onHide?.(focusedStartDate, focusedEndDate);
          }}
          disabled={!focusedStartDate || !focusedEndDate}
          border={false}
        />
      </div>
    </div>
  );
};

export default RangeCalendarBox;
